import React, { useState, useEffect } from "react";
import "./SoPrintTemplate.css";

export default function SoPrintTemplateLogistics({ so }) {
  const [copyType, setCopyType] = useState("LOGISTICS COPY");

  useEffect(() => {
    const handler = (e) => setCopyType(e.detail || "LOGISTICS COPY");
    window.addEventListener("SET_COPY_TYPE", handler);
    return () => window.removeEventListener("SET_COPY_TYPE", handler);
  }, []);

  if (!so) return null;

  const items = so.items || [];

  const formatMoney = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatQty = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const subtotal = Number(so.Subtotal || 0);
  const vat = Number(so.VAT || 0);
  const grandTotal = Number(so.GrandTotal || subtotal + vat);

  return (
    <div className="so-print">
      {/* HEADER */}
      <div className="so-header-row-1">
        <img
          src="/img/biocare-logo.png"
          alt="Biocare Logo"
          className="so-logo"
        />

        <div className="so-company-text">
          <strong>BIOCARE, INC.</strong><br />
          Room 306 WEBJET Bldg., 64 Quezon Avenue,<br />
          B.M.A. Ave., Tatalon, Quezon City<br />
          Telephone: 8732-0535, 8712-0531<br />
          8714-9159, 8712-5389<br />
          Fax: (02) 8711-4310<br />
          VAT REG. TIN: 006-163-782-000
        </div>

        <div className="so-header-right">
          <div className="so-ref-label">REFER TO THIS NUMBER</div>

          <table className="so-num-table">
            <thead>
              <tr>
                <th>DAY</th>
                <th>MO</th>
                <th>YR</th>
                <th>NUMBER</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{so.SODate?.slice(8, 10)}</td>
                <td>{so.SODate?.slice(5, 7)}</td>
                <td>{so.SODate?.slice(2, 4)}</td>
                <td className="so-number">{so.SONumber}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="so-header-row-2">
        <div className="so-title">SALES ORDER</div>
      </div>

      {/* SOLD TO / SHIP TO */}
      <div className="so-contact-row">
        <div className="contact-col">
          <div className="contact-label">SOLD TO</div>
          <div className="contact-text">{so.CustomerName}</div>
        </div>

        <div className="contact-col">
          <div className="contact-label">SHIP TO</div>
          <div className="contact-text">{so.ShipTo}</div>
        </div>

        <div className="contact-col">
          <div className="contact-label">MARK TO</div>
          <div className="contact-text">{so.MarkTo || ""}</div>
        </div>
      </div>

      {/* CUSTOMER INFO */}
      <table className="so-customer-info">
        <tbody>
          <tr>
            <td>
              <div className="label">CUSTOMER NUMBER</div>
              <div className="value">{so.CustomerNumber || ""}</div>
            </td>

            <td>
              <div className="label">AREA CODE</div>
              <div className="value">{so.AreaManagerCode || ""}</div>
            </td>

            <td>
              <div className="label">REFERENCE ORDER</div>
              <div className="value">{so.ReferenceOrder || ""}</div>
            </td>

            <td>
              <div className="label">DATE</div>
              <div className="value">
                {so.SODate
                  ? new Date(so.SODate).toLocaleDateString("en-PH")
                  : ""}
              </div>
            </td>

            <td>
              <div className="label">SHIPPING INSTRUCTION</div>
              <div className="value">{so.ShippingInstruction || ""}</div>
            </td>

            <td>
              <div className="label">TERMS OF PAYMENT</div>
              <div className="value">{so.TermsOfPayment || ""}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SPECIAL INSTRUCTIONS */}
      <div className="so-special">
        <div className="label">SPECIAL INSTRUCTIONS</div>
        <div className="value">{so.SpecialInstruction || ""}</div>
      </div>

      {/* ITEM TABLE */}
      <table className="so-item-table">
        <thead>
          <tr>
            <th>QTY</th>
            <th>UNIT</th>
            <th>CATALOG</th>
            <th>DESCRIPTION</th>
            <th>LOT #</th>
            <th>EXPIRY</th>
            <th>UNIT PRICE</th>
            <th>EXTENSION</th>
          </tr>
        </thead>

        <tbody>
          {items.map((it, idx) => {
            const lots =
              it.lots && it.lots.length > 0
                ? it.lots
                : [
                    {
                      LotNumber: it.LotNumber || "",
                      ExpiryDate: it.ExpiryDate || "",
                    },
                  ];

            const group = {};
            lots.forEach((l) => {
              const ln = l.LotNumber || "";
              if (!group[ln]) {
                group[ln] = { count: 1, dates: [l.ExpiryDate] };
              } else {
                group[ln].count++;
                group[ln].dates.push(l.ExpiryDate);
              }
            });

            const mergedLots = Object.entries(group)
              .map(([ln, g]) => (g.count > 1 ? `${ln} (${g.count})` : ln))
              .join("\n");

            const mergedExpiry = Object.values(group)
              .map((g) => {
                const sorted = g.dates.filter(Boolean).sort();
                return sorted[0] || "";
              })
              .join("\n");

            const totalExtension =
              it.InitialExtensionPrice !== undefined &&
              it.InitialExtensionPrice !== null
                ? Number(it.InitialExtensionPrice)
                : Number(it.InitialPrice || 0) * Number(it.Quantity || 0);

            return (
              <tr key={idx}>
                <td>{formatQty(it.Quantity)}</td>
                <td>{it.UnitOfMeasure}</td>
                <td>{it.CatalogNo}</td>
                <td>{it.ItemName}</td>

                <td style={{ whiteSpace: "pre-line" }}>{mergedLots}</td>
                <td style={{ whiteSpace: "pre-line" }}>{mergedExpiry}</td>

                <td>{formatMoney(it.InitialPrice)}</td>
                <td>{formatMoney(totalExtension)}</td>
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr>
            <td colSpan="6"></td>
            <td>SUBTOTAL</td>
            <td>{formatMoney(subtotal)}</td>
          </tr>

          <tr>
            <td colSpan="6"></td>
            <td>VAT (12%)</td>
            <td>{formatMoney(vat)}</td>
          </tr>

          <tr>
            <td colSpan="6"></td>
            <td className="grand-total">GRAND TOTAL</td>
            <td className="grand-total">{formatMoney(grandTotal)}</td>
          </tr>
        </tfoot>
      </table>

      {/* FOOTER */}
      <div className="so-clauses">
        <strong>SPECIAL CLAUSE(S): </strong>
        Provided that the customer has no pending clause and items can be returned…
      </div>

      <div className="so-collection-row">
        <div className="collection-left">
          <div className="label">COLLECTION DATE</div>
          <div className="line">AMOUNT REMITTED ____________________________</div>
          <div className="line">INVOICE NO. ________________________________</div>
          <div className="line">OFFICIAL RECEIPT NO. ________________________</div>
          <div className="line">DATE OF OFFICIAL RECEIPT _____________________</div>
        </div>

        <div className="collection-right">
          <div className="line">CONFORME _____________________________________</div>
        </div>
      </div>

      <div className="copy-note">{copyType}</div>
    </div>
  );
}